import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import FloatingTooltip from 'LuxKit/FloatingTooltip'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Typography from 'LuxKit/Typography/Typography'
import { diffInDays } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import { useMemo, useRef } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

const Cell = styled(Clickable)`
  position: relative;
  min-height: ${rem(60)};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  padding-block: ${rem(4)} 0;
  padding-inline: ${rem(2)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &:active {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.type-check-in, &.type-check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.type-between {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.unselectable {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    cursor: not-allowed;

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &.type-between.unselectable {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }

  &.type-none.variant-unavailable, &.type-none.variant-no-availability {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      }
    }
    &:active {
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    }
  }
`

const DurationLabel = styled(StructureLabel)`
  position: absolute;
  inset-inline-end: -${rem(20)};
  top: ${rem(6)};
  z-index: 1;
  transform: translateY(-100%);
`

const FeaturedIcon = styled(SolidFireIcon)`
  position: absolute;
  top: 0;
  inset-inline-end: ${rem(2)};
`

const Content = styled(Typography)`
  opacity: 0;

  &.visible {
    opacity: 1;
  }
`

export type OfferCalendarSidebarDayCellType = 'check-in' | 'check-out' | 'between' | 'none'
export type OfferCalendarSidebarDayCellVariant = 'price-member' | 'price-cheapest' | 'price' | 'none' | 'sold-out-rooms' | 'sold-out-recommendations' | 'sold-out-hotel' | 'unavailable' | 'no-availability'
export type OfferCalendarSidebarDayCellFeatured = 'special' | 'loyalty'
interface Props {
  /** The raw calendar data for a day */
  day: App.CalendarDay
  /**
   * Whether or not we have flights on or not
   */
  flights?: boolean
  /**
   * Event for when the cell is clicked, provides the day object to the listener
   */
  onClick?: (day: App.CalendarDay) => void
  /**
   * The purpose the cell is for, determines which components
   * in the cell are shown
   */
  variant: OfferCalendarSidebarDayCellVariant
  /**
   * The 'state' the cell is currently in
   * Determines mostly how the *cell* looks
   */
  type: OfferCalendarSidebarDayCellType
  /**
   * Whether or not this is a featured "day"
   * Will apply the appropriate icon to make it look significant
   */
  featured?: OfferCalendarSidebarDayCellFeatured
  /**
   * The price to display for the cell
   */
  price?: number
  duration?: number
  isCheapestFlightPrice?: boolean
  showContentOnBetweenDay?: boolean
  unselectable?: boolean
  minimumStayTooltipOpen?: boolean
  minimumStayTooltipDescription?: string
  maximumStayTooltipOpen?: boolean
  maximumStayTooltipDescription?: string
  style?: React.CSSProperties
}

function OfferCalendarSidebarDayCell(props: Props) {
  const {
    day,
    flights,
    onClick = noop,
    variant,
    type,
    featured,
    price = 0,
    duration,
    isCheapestFlightPrice,
    showContentOnBetweenDay = false,
    unselectable = false,
    minimumStayTooltipOpen = false,
    minimumStayTooltipDescription,
    maximumStayTooltipOpen = false,
    maximumStayTooltipDescription,
    style,
  } = props
  const intl = useIntl()
  const cellRef = useRef<HTMLButtonElement>(null)

  const priceColour = useMemo<React.ComponentProps<typeof Caption>['colour']>(() => {
    if (isCheapestFlightPrice && type !== 'check-in') {
      return 'highlight-primary-dark'
    } else if (variant === 'price-member') {
      return 'lux-plus'
    } else if (variant === 'price-cheapest' && type !== 'check-in') {
      return 'highlight-primary-dark'
    }
    return 'light-foreground-alt'
  }, [isCheapestFlightPrice, variant, type])

  const showContent = type !== 'between' || showContentOnBetweenDay
  const displayDuration = duration ?? diffInDays(new Date(day.checkIn), new Date(day.checkOut))

  const soldOutText = useMemo(() => {
    switch (variant) {
      case 'sold-out-hotel':
        return <FormattedMessage
          id="ym89XYTFnhfKCZjbCrZg"
          defaultMessage="Hotel{br}only"
          description="Calendar day cell text when only hotel-only option is available"
          values={{ br: <br /> }}
        />
      case 'sold-out-rooms':
        return <FormattedMessage
          id="fCPg2YjFdc00BYQi8Xij"
          defaultMessage="View{br}rooms"
          description="Calendar day cell text to view available rooms on the offer detail page"
          values={{ br: <br /> }}
        />
      case 'sold-out-recommendations':
        return <FormattedMessage
          id="1LSdP2c9xkUo6qPaohCq"
          defaultMessage="More{br}offers"
          description="Calendar day cell text for more offer recommendations"
          values={{ br: <br /> }}
        />
      default:
        return undefined
    }
  }, [variant])

  const [tier] = useCurrentLoyaltyTier()

  return <>
    <Cell
      ref={cellRef}
      style={style}
      onClick={() => onClick(day)}
      className={clsx(
        `type-${type}`,
        `variant-${variant}`,
        { unselectable },
      )}
    >
      {featured === 'special' && type !== 'between' && <FeaturedIcon colour="urgency" size="XXS" />}
      {featured === 'loyalty' && type !== 'between' && <FeaturedIcon as={SolidCircleIcon} size="XXS" colour={`tier-${tier}`} />}
      {type === 'check-out' && <DurationLabel variant="standard">
        {intl.formatMessage({
          id: 'eXCuKdiINHYKUP4JF6R0',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Duration label on calendar sidebar checkout day cell',
        }, { count: displayDuration })}
      </DurationLabel>}
      <BodyText variant="medium" align="center">{day.day}</BodyText>
      <Content align="center" className={clsx({ visible: showContent })}>
        {(variant === 'price' || variant === 'price-cheapest' || variant === 'price-member') && <Caption
          variant="smallalt"
          tabletVariant="medium"
          colour={priceColour}
        >
          <FormattedMessage
            id="Kp21kKgCgjuD8wMv4MNe"
            defaultMessage="{price}{asterisk}"
            description="Price with optional asterisk indicating flights are included"
            values={{
              price: <FormatCurrency
                value={price}
                format="dynamic"
                notation={price > 10000 ? 'compact' : undefined}
                maximumFractionDigits={price > 10000 ? 1 : undefined}
              />,
              asterisk: flights ? '*' : '',
            }}
          />
        </Caption>}
        {!!soldOutText && <Caption variant="smallalt" tabletVariant="medium" colour="light-foreground-alt" underline>
          {soldOutText}
        </Caption>}
        {variant === 'no-availability' && <Caption variant="smallalt" tabletVariant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="bLLX9bjKBF-iuG4qIcnw"
            defaultMessage="No availability"
            description="Message shown when a fixed length stay calendar date combination is unavailable."
          />
        </Caption>}
      </Content>
    </Cell>
    {minimumStayTooltipDescription && <FloatingTooltip
      description={minimumStayTooltipDescription}
      triggerRef={cellRef}
      open={minimumStayTooltipOpen}
      placement="top"
    />}
    {maximumStayTooltipDescription && <FloatingTooltip
      description={maximumStayTooltipDescription}
      triggerRef={cellRef}
      open={maximumStayTooltipOpen}
      placement="top"
    />}
  </>
}

export default React.memo(OfferCalendarSidebarDayCell)
